/** Confirmation dialog for cancelling a treatment with a mandatory reason. */
import { useState } from 'react'
import { toast } from 'sonner'

import type { TreatmentWithRelations } from '@snootsvet/shared'

import { Button } from '@/frontend/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import { Textarea } from '@/frontend/components/ui/textarea'
import { useCancelTreatment } from '@/frontend/hooks/api/patients/use-cancel-treatment'

export function CancelTreatmentDialog({
  rx,
  patientId,
  open,
  onOpenChange,
  nested = false,
}: {
  rx: TreatmentWithRelations
  patientId: string
  open: boolean
  nested?: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [reason, setReason] = useState('')
  const { mutateAsync: cancelTreatment, isPending } = useCancelTreatment()

  async function handleConfirm() {
    try {
      await cancelTreatment({ treatmentId: rx.id, reason, patientId })
      toast.success('Treatment cancelled')
      onOpenChange(false)
      setReason('')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel treatment')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent nested={nested}>
        <DialogHeader>
          <DialogTitle>Cancel Treatment</DialogTitle>
          <DialogDescription>
            This will cancel the active prescription for {rx.productInfo.name}. This action cannot
            be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <label htmlFor="cancel-reason" className="font-medium">
              Reason <span className="text-destructive-fg">*</span>
            </label>
            <Textarea
              id="cancel-reason"
              placeholder="e.g. Owner requested discontinuation, adverse reaction…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Keep Treatment
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isPending || reason.trim().length === 0}
          >
            {isPending ? 'Cancelling…' : 'Cancel Treatment'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
